"use client";

import { Menu, Transition } from "@headlessui/react";
import { Link } from "next-view-transitions";
import { usePathname } from "next/navigation";
import { Fragment, useRef } from "react";
import { twMerge } from "tailwind-merge";

import { PopoverButton } from "./PopoverButton";
import { TipBadge } from "./TipBadge";

type MenuItem = {
  name: string;
  href: string;
  label: string;
};

type Props = {
  name: string;
  items: MenuItem[];
  label?: string;
  badgeText?: string;
};

/**
 * follow this github issue solution to make menu hoverable:
 * https://github.com/tailwindlabs/headlessui/issues/239#issuecomment-778746069
 */
export const NavbarMenu = ({ name, items, label, badgeText }: Props) => {
  const pathname = usePathname();
  const isActive = items.some(({ href }) => pathname.includes(href));

  const buttonRef = useRef<HTMLButtonElement>(null);
  let timeout: NodeJS.Timeout;

  const toggleMenu = (open: boolean) => {
    clearTimeout(timeout);
    if (open) {
      timeout = setTimeout(() => buttonRef?.current?.click(), 50);
    }
  };

  return (
    <Menu as="div" className="relative">
      {({ open }) => (
        <div
          onMouseEnter={() => toggleMenu(!open)}
          onMouseLeave={() => toggleMenu(open)}
        >
          <Menu.Button
            className={twMerge(
              "rounded-md px-3 py-2 text-sm font-medium transition focus:outline-none",
              isActive
                ? "bg-zinc-900/40 text-white"
                : "text-gray-300 hover:bg-zinc-900/40 hover:text-white"
            )}
            ref={buttonRef}
            aria-label={`open ${label} menu`}
          >
            {name}
          </Menu.Button>
          {badgeText && (
            <TipBadge
              text={badgeText}
              className="absolute -right-5 -top-2 z-10"
            />
          )}
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              className="absolute z-10 mt-2 w-48 origin-top-left rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
              aria-label={`${label} menu`}
            >
              {items.map(({ name, href, label }) => (
                <Menu.Item key={name}>
                  {({ active, close }) => (
                    <Link
                      href={href}
                      onClick={close}
                      aria-label={label}
                      prefetch
                    >
                      <PopoverButton
                        className={twMerge(
                          (active || pathname === href) && "bg-gray-100"
                        )}
                      >
                        {name}
                      </PopoverButton>
                    </Link>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </div>
      )}
    </Menu>
  );
};
